import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Result, Spin } from 'antd'
import logo from '@/assests/Shakthi Power outline logo.png'
import { authStore, type SoftwareCode } from '@/context/auth'
import './LoginPage.css'
import './SelectSoftwarePage.css'

// Where the Grand Shakthi Power frontend is deployed. Configure per
// environment via VITE_GRAND_SHAKTHI_URL (see .env.example).
const GRAND_SHAKTHI_URL =
  (import.meta.env.VITE_GRAND_SHAKTHI_URL as string | undefined) || 'http://localhost:3001'

const CARDS: Record<SoftwareCode, { icon: string; iconClass: string; title: string; desc: string }> = {
  SHAKTHI_POWER: { icon: 'SP', iconClass: 'ssp-card-icon-blue', title: 'Shakthi Power', desc: 'Sales, Allocations & Financial ERP' },
  GRAND_SHAKTHI_POWER: { icon: 'GSP', iconClass: 'ssp-card-icon-amber', title: 'Grand Shakthi Power', desc: 'Purchase, Inventory & HR ERP' },
}

// The redirect/selection hub every login lands on. Behaviour depends
// entirely on the signed-in user's software access (Employee Master's
// Software Access tab, embedded in the JWT at login):
//   0 apps → blocked, no redirect anywhere
//   1 app  → silently redirected there, this page is never actually seen
//   2 apps → both cards shown, user picks
const SelectSoftwarePage: React.FC = () => {
  const navigate = useNavigate()
  const user = authStore.getUser()
  const access = user?.softwareAccess ?? []
  // Tracks which card is being opened, if any — drives the loading view
  // below instead of a blank/unresponsive moment while a cross-origin
  // navigation (Grand Shakthi Power's SSO handoff) is in flight, which can
  // take a couple of seconds.
  const [redirecting, setRedirecting] = useState<SoftwareCode | null>(access.length === 1 ? access[0] : null)

  const openGrandShakthiPower = () => {
    const token = authStore.getToken()
    if (!token) {
      navigate('/login')
      return
    }
    // Cross-origin hand-off: Grand Shakthi Power reads this one-time token
    // from the URL, stores it, and strips it from the address bar — the
    // user is never asked to log in a second time.
    window.location.href = `${GRAND_SHAKTHI_URL}/sso?token=${encodeURIComponent(token)}`
  }

  const openSoftware = (code: SoftwareCode) => {
    setRedirecting(code)
    if (code === 'SHAKTHI_POWER') navigate('/')
    else openGrandShakthiPower()
  }

  // Single-access accounts skip this screen entirely — redirect as soon as
  // we know where to, instead of flashing a card grid the user never gets
  // to click.
  useEffect(() => {
    if (access.length === 1) openSoftware(access[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLogout = () => {
    authStore.clear()
    navigate('/login')
  }

  if (redirecting) {
    return (
      <div className="lp-root-centered">
        <div className="lp-bg-glow glow-1" />
        <div className="lp-bg-glow glow-2" />
        <div className="lp-bg-glow glow-3" />
        <div className="ssp-container">
          <div className="lp-logo-header">
            <img src={logo} alt="Shakthi Power Logo" className="lp-logo-main" />
          </div>
          <div className="ssp-redirect">
            <Spin size="large" />
            <p>Opening {CARDS[redirecting].title}…</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="lp-root-centered">
      <div className="lp-bg-glow glow-1" />
      <div className="lp-bg-glow glow-2" />
      <div className="lp-bg-glow glow-3" />

      <div className="ssp-container">
        <div className="lp-logo-header">
          <img src={logo} alt="Shakthi Power Logo" className="lp-logo-main" />
        </div>

        {access.length === 0 ? (
          <Result
            status="403"
            title="No software access"
            subTitle="You do not currently have access to any software. Please contact your administrator."
            extra={
              <Button type="primary" onClick={handleLogout}>
                Log out
              </Button>
            }
          />
        ) : (
          <>
            <div className="ssp-heading">
              <h2>Choose a Software</h2>
              <p>{user ? `Welcome back, ${user.name}` : 'Select which application you want to open'}</p>
            </div>

            <div className="ssp-card-grid">
              {access.map((code) => {
                const card = CARDS[code]
                return (
                  <button key={code} type="button" className="ssp-card" onClick={() => openSoftware(code)}>
                    <div className={`ssp-card-icon ${card.iconClass}`}>{card.icon}</div>
                    <div className="ssp-card-title">{card.title}</div>
                    <div className="ssp-card-desc">{card.desc}</div>
                    <span className="ssp-card-arrow" aria-hidden="true">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </button>
                )
              })}
            </div>
          </>
        )}

        <div className="lp-footer-text">
          © {new Date().getFullYear()} Shakthi Power ERP. All rights reserved.
        </div>
      </div>
    </div>
  )
}

export default SelectSoftwarePage
